import FlagProvider, {
  type IConfig,
  useFlagsStatus,
  useUnleashClient,
  useUnleashContext,
} from '@unleash/proxy-client-react';
import { useEffect } from 'react';
import { create } from 'zustand';

import { useGetLoggedInUser } from '@revelio/auth';
import { unleashUserProperties } from '@revelio/iso-utility';

import { environment } from '../../environments/environment';
import { UnleashFallback } from './unleash-fallback';

const unleashedConfig: IConfig = {
  url: environment.UNLEASH_URL,
  clientKey: environment.UNLEASH_CLIENT_KEY,
  refreshInterval: 15,
  appName: 'reveliolabs-dashboard',
};

export const UnleashFlags = ({ children }: { children: React.ReactNode }) => {
  return (
    <FlagProvider config={unleashedConfig}>
      <UnleashFallback>{children}</UnleashFallback>
      <SyncUnleashClientName />
    </FlagProvider>
  );
};

const useUnleashUserContextStore = create<{
  synced: boolean;
  markSynced: () => void;
}>((set) => ({
  synced: false,
  markSynced: () => set({ synced: true }),
}));

/**
 * True once flag values can be TRUSTED for the logged-in user: Unleash has
 * answered (`flagsReady`) AND the client context carries the user id with the
 * re-fetch for it completed. `flagsReady` alone is NOT enough for user-targeted
 * flags — the first fetch runs against the anonymous context, so a flag like
 * User-targeted flags read false for ~1s after `flagsReady` and then flip once
 * `SyncUnleashClientName`'s context update lands. Boot-gated UI (theme marker,
 * homepage switch) must wait for THIS signal or it acts on the wrong values and
 * flashes. On Unleash error, reports ready so the app falls through to
 * fallback-provider values instead of holding forever.
 */
export const useUnleashUserFlagsReady = (): boolean => {
  const { flagsReady, flagsError } = useFlagsStatus();
  const synced = useUnleashUserContextStore((s) => s.synced);
  return (flagsReady && synced) || Boolean(flagsError);
};

/** Failsafe so a hung context re-fetch can't hold boot-gated UI forever. */
const SYNC_FAILSAFE_MS = 5000;

const SyncUnleashClientName = () => {
  const { loggedInUser } = useGetLoggedInUser();
  const updateContext = useUnleashContext();
  const client = useUnleashClient();
  const markSynced = useUnleashUserContextStore((s) => s.markSynced);

  useEffect(() => {
    const hasUser = Boolean(loggedInUser?.id);
    let cancelled = false;
    let fallback: ReturnType<typeof setTimeout> | undefined;
    const mark = () => {
      if (!cancelled) {
        markSynced();
      }
    };
    void Promise.resolve(
      updateContext({
        userId: loggedInUser?.id ?? undefined,
        properties: unleashUserProperties(loggedInUser),
      })
    ).then(() => {
      // Only a context update that actually carried the user id makes flag
      // values trustworthy; the initial anonymous update must not mark.
      if (cancelled || !hasUser) {
        return;
      }
      // Mark on the client's next `update` tick, NOT here: the promise
      // resolves ~a frame before the new toggles propagate into the React
      // flag hooks, and marking early un-holds boot-gated UI against stale
      // values for one render (measured as an ~80ms theme strip). The
      // update-event listener runs in the same tick as the flag hooks'
      // re-render, so React batches both into one consistent render. The
      // short fallback covers a fetch whose toggles were unchanged (no
      // update event — and no race either, since nothing moved).
      client.once('update', mark);
      fallback = setTimeout(mark, 500);
    });
    const failsafe = hasUser ? setTimeout(mark, SYNC_FAILSAFE_MS) : undefined;
    return () => {
      cancelled = true;
      client.off('update', mark);
      if (fallback !== undefined) {
        clearTimeout(fallback);
      }
      if (failsafe !== undefined) {
        clearTimeout(failsafe);
      }
    };
  }, [loggedInUser, updateContext, client, markSynced]);
  return null;
};
